/**
 * Utility functions, types, and data logic for ChartInfoSection
 */

import { JatakaResponse } from '../../../../../models/api';
import { SharedChartData, SharedViewMode } from '../../../../../types/share.types';
import { AccordionItem } from '../types';
import { decimalToDMS, formatDMS } from '../../../../../utils/privacyUtils';

// Basic info type
export type BasicInfo = {
    chart_name?: string;
    location?: string;
    place_name?: string;
    date?: string;
    time?: string;
    datetime_iso?: string;
    timezone?: string;
    altitude?: string;
    latitudeValue?: number;
    longitudeValue?: number;
};

/**
 * Format coordinates from decimal to unified DMS display
 */
export const formatCoordinatesDisplay = (lat: number, lon: number): string => {
    if (!lat && !lon) return '';
    const latDMS = decimalToDMS(lat, 'lat');
    const lonDMS = decimalToDMS(lon, 'lon');
    return `${formatDMS(latDMS)}, ${formatDMS(lonDMS)}`;
};

/**
 * Extract basic info data from API response
 */
export function extractChartInfoData(response: JatakaResponse): { basic_info: BasicInfo } | null {
    return {
        basic_info: response.basic_info as BasicInfo
    };
}

/**
 * Get global showLocation setting
 */
export const getShowLocationSetting = (): boolean => {
    // Check window global first (set by SettingsPanel)
    if (typeof window !== 'undefined' && typeof window.globalShowLocation !== 'undefined') {
        return window.globalShowLocation;
    }
    // Fallback to localStorage
    try {
        const saved = localStorage.getItem('hora-showLocation');
        return saved === null ? true : saved === 'true';
    } catch {
        return true;
    }
};

/**
 * Mask seconds in coordinate string for privacy
 * Input: "34 E 32' 59", 48 N 35' 42""
 * Output: "34 E 32' **", 48 N 35' **""
 */
export const maskCoordinateSeconds = (location: string): string => {
    // Replace seconds (digits before ") with asterisks
    return location.replace(/(\d+)"/g, '**"');
};

/**
 * Generate accordion items for Chart Info section
 */
export const generateChartInfoItems = (
    data: { basic_info: BasicInfo },
    sharedViewMode?: SharedViewMode,
    sharedChartData?: SharedChartData,
    showLocationSetting: boolean = true
): AccordionItem[] => {
    const { basic_info } = data;
    const items: AccordionItem[] = [];

    if (!basic_info) return items;

    // Get share config for field filtering (guest mode)
    const shareConfig = sharedChartData?.share_config;
    const isGuestMode = sharedViewMode === 'guest';

    // Build field visibility map
    const fieldVisibility: Record<string, boolean> = {
        chart_name: true,
        location: showLocationSetting, // Controlled by settings
        place_name: true,
        date: true,
        time: true,
        timezone: true,
        altitude: true
    };

    // Apply guest mode filtering
    if (isGuestMode && shareConfig?.fields) {
        const fields: any = shareConfig.fields;
        if (!(fields.name ?? true)) fieldVisibility.chart_name = false;
        if (!(fields.birthPlace ?? fields.birth_place ?? true)) {
            fieldVisibility.location = false;
            fieldVisibility.place_name = false;
        }
        if (!(fields.birthTime ?? fields.birth_time ?? true)) {
            fieldVisibility.date = false;
            fieldVisibility.time = false;
        }
        if (!(fields.timezone ?? true)) fieldVisibility.timezone = false;
        if (!(fields.coordinates ?? true)) fieldVisibility.altitude = false;
    }

    // Use unified DMS format for coordinates display
    const locationDisplay = (basic_info.latitudeValue && basic_info.longitudeValue)
        ? formatCoordinatesDisplay(basic_info.latitudeValue, basic_info.longitudeValue)
        : basic_info.location || '';

    // Create details array with visibility metadata
    const details: Array<{ label: string; value: string | number; fieldKey?: string; editable?: boolean; visible: boolean }> = [
        { label: 'Chart name', value: basic_info.chart_name || '', fieldKey: 'chart_name', editable: true, visible: fieldVisibility.chart_name },
        { label: 'Location', value: locationDisplay, visible: fieldVisibility.location },
        { label: 'Place', value: basic_info.place_name || '', fieldKey: 'place_name', editable: true, visible: fieldVisibility.place_name },
        { label: 'Date', value: basic_info.date || '', visible: fieldVisibility.date },
        { label: 'Time', value: basic_info.time || '', visible: fieldVisibility.time },
        { label: 'Timezone', value: basic_info.timezone || '', visible: fieldVisibility.timezone },
        { label: 'Altitude', value: basic_info.altitude || '', visible: fieldVisibility.altitude }
    ];

    // Filter visible fields and map to AccordionDetail format
    const visibleDetails = details
        .filter(d => d.visible && (d.value || d.editable))
        .map(d => ({ label: d.label, value: d.value || 'N/A' }));

    items.push({
        id: 'chart-info',
        name: 'Chart Info',
        short_name: 'CI',
        type: 'info',
        details: visibleDetails,
        metadata: { fieldVisibility, basicInfo: basic_info }
    });

    return items;
};
